import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import warning from "@/assets/warning.svg";
import { MoreVertical, Pencil, Trash2, Printer, Eye } from "lucide-react";
import React from "react";
import { FacilityBooking } from "@/types/facility/booking";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setInvoice } from "@/redux/invoiceSlice";
import { InvoiceExport, ReceiptExport } from "../../pos/sales_history/table/receipt-component";
import { useLazyGetInvoiceByIdQuery } from "@/services/invoiceApi";
import { ErrorType } from "@/types";
import { useLazyGetTransactionByIdQuery } from "@/services/transactionApi";
import { Transaction } from "@/types/pos/transaction";
import { InvoiceStatus } from "@/types/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

export function DataTableRowActions<TData>({
  data,
  refetch,
  handleEdit,
  handleDelete,
  handleConfirmBooking,
  access,
}: {
  data: FacilityBooking;
  refetch: () => void;
  handleEdit: (data: FacilityBooking) => void;
  handleDelete: (data: FacilityBooking) => void;
  handleConfirmBooking: (data: FacilityBooking) => void;
  access: string;
}) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isdelete, setIsDelete] = React.useState(false);

  // Use lazy query for transaction data - only call when needed
  const [getTransactionById, { data: transactionData, isLoading: isLoadingTransaction, error: transactionError }] = useLazyGetTransactionByIdQuery();

  // Use lazy query for invoice data - only call when needed
  const [getInvoiceById, { data: invoiceData, isLoading: isLoadingInvoice, error: invoiceError }] = useLazyGetInvoiceByIdQuery();

  const deleteRow = async () => {
    handleDelete(data);
  };

  async function buildPrintHtml(currentInvoiceData: any, currentTransactionData?: Transaction): Promise<string> {
    if (currentInvoiceData?.status === InvoiceStatus.Paid && currentInvoiceData.transaction_id != null) {
      return await ReceiptExport(currentTransactionData as Transaction);
    } else if (currentInvoiceData) {
      return (await InvoiceExport(currentInvoiceData));
    } else {
      throw new Error("Invoice data not available");
    }
  }

  function printHtml(htmlContent: string) {
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    iframe.style.visibility = "hidden";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
    if (!iframeDoc) return;
    iframeDoc.open();
    iframeDoc.write(htmlContent || "");
    iframeDoc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  }

  const handlePrint = async () => {
    try {
      let currentInvoiceData = invoiceData;
      let currentTransactionData = transactionData;

      // If we don't have invoice data yet, fetch it
      if (!currentInvoiceData && data.invoice_id) {
        const result = await getInvoiceById({ id: data.invoice_id, org_id: orgId });

        if (result.error) {
          toast({
            variant: "destructive",
            title: "Error Loading Invoice",
            description: "Failed to load invoice data. Please try again.",
          });
          return;
        }

        currentInvoiceData = result.data;
      }

      if (!currentInvoiceData) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Invoice data not available for printing",
        });
        return;
      }

      // If this is a paid invoice and we need transaction data but don't have it yet
      if (currentInvoiceData.status === InvoiceStatus.Paid && currentInvoiceData.transaction_id != null && !currentTransactionData) {
        // Fetch transaction data
        const result = await getTransactionById({ transaction_id: currentInvoiceData.transaction_id, org_id: orgId });

        if (result.error) {
          toast({
            variant: "destructive",
            title: "Error Loading Transaction",
            description: "Failed to load transaction data. Please try again.",
          });
          return;
        }

        // Use the fetched data
        currentTransactionData = result.data;
      }

      // Generate and print the HTML
      const html = await buildPrintHtml(currentInvoiceData, currentTransactionData);
      printHtml(html);

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Print Error",
        description: "Failed to generate print content. Please try again.",
      });
    }
  };



  const handlePay = async () => {
    try {
      let currentInvoiceData = invoiceData;

      // If we don't have invoice data yet, fetch it
      if (!currentInvoiceData && data.invoice_id) {
        const result = await getInvoiceById({ id: data.invoice_id, org_id: orgId });

        if (result.error) {
          toast({
            variant: "destructive",
            title: "Error Loading Invoice",
            description: "Failed to load invoice data. Please try again.",
          });
          return;
        }

        currentInvoiceData = result.data;
      }

      if (!currentInvoiceData) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Invoice data not available for payment",
        });
        return;
      }

      dispatch(setInvoice(currentInvoiceData));

      setTimeout(() => {
        navigate('/counter-selection');
      }, 500);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load invoice data for payment.",
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-40"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleConfirmBooking(data);
            }}
          >
            <Eye className="mr-2 h-4 w-4" />
            View
          </DropdownMenuItem>
          {(data.status == "Pending" && data.payment_status != "Paid") && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(data);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          {/* Invoice Actions - only show if invoice_id exists */}
          {data.invoice_id && (
            <>
              <DropdownMenuItem
                className="text-nowrap"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrint();
                }}
                disabled={isLoadingTransaction || isLoadingInvoice}
              >
                {isLoadingTransaction || isLoadingInvoice ? (
                  <i className="fa fa-spinner fa-spin mr-2 h-4 w-4"></i>
                ) : (
                  <Printer className="mr-2 h-4 w-4" />
                )}
                {isLoadingTransaction || isLoadingInvoice ? "Loading..." : "Print"}
              </DropdownMenuItem>

              {data.payment_status === "Pending" && (
                <DropdownMenuItem
                  className="text-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePay();
                  }}
                  disabled={isLoadingInvoice}
                >
                  {isLoadingInvoice ? (
                    <i className="fa fa-spinner fa-spin mr-2 h-4 w-4"></i>
                  ) : (
                    <Printer className="mr-2 h-4 w-4" />
                  )}
                  {isLoadingInvoice ? "Loading..." : "Pay"}
                </DropdownMenuItem>
              )}
            </>
          )}

          {access == "full_access" && data.status == "Pending" && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setIsDelete(true);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isdelete && (
        <AlertDialog open={isdelete} onOpenChange={setIsDelete}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-center  justify-center gap-4">
                  <img src={warning} alt="warning" className="w-18 h-18" />
                  <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                    Please confirm if you want to delete this Facility Booking
                  </AlertDialogTitle>
                </div>
                <div className="w-full flex justify-between items-center gap-3 mt-4">
                  <AlertDialogCancel
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDelete(false);
                    }}
                    className="w-full border border-primary font-semibold"
                  >
                    <i className="fa fa-xmark text-base px-1 "></i>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRow();
                    }}
                    className="w-full bg-primary !text-black font-semibold"
                  >
                    <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                    Delete
                  </AlertDialogAction>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
